import useAppStore from "@/context/store";
import { useCheckVipUpgrade } from "@/pageComponents/VipUpgradeModal";
import { getGrocery, getQingzhouConfig } from "@/services";
import { parseVipUrlHash } from "@/utils/gaodingVip";
import { useGlobalMerge } from "@/utils/gdMerge";
import { PRESETS_CONDITION, stewardInstance } from "@/utils/steward";
import { useMemoizedFn } from "ahooks";
import dayjs from "dayjs";
import { isNumber } from "lodash";
import { useRouter } from "next/router";
import { useEffect } from "react";
import HBMessage from "../HBMessage";
import { destroyAll } from "../HBModal/ModalWrap";
import { useLogin } from "../LoginModal";
import { AdditionGoodDialog, BuyVipDialog, VipAuthDialog } from "../Vip";

// 第一次弹框Hook, 用于处理进入页面时需要弹框的逻辑
const useFirstModal = function () {
  const { vips, user, userReady, downloadLeft, orgInfo } = useAppStore(
    ({ vips, user, userReady, downloadLeft, orgInfo }) => ({
      vips,
      user,
      userReady,
      downloadLeft,
      orgInfo,
    })
  );
  const { login } = useLogin();
  const router = useRouter();

  useEffect(() => {
    const skipMerge = router.query.skipMerge;
    if (skipMerge === "1") {
      localStorage.setItem("skipMerge", "1");
    }

    if (skipMerge === "0") {
      localStorage.removeItem("skipMerge");
    }
  }, [router.query]);

  // 购买vip弹框通过hash地址唤起
  const handleBuyVipHash = useMemoizedFn(async () => {
    const actionsMap = {
      buyVip: BuyVipDialog.open,
      buyVipAuthExtend: VipAuthDialog.open,
      buyAddition: AdditionGoodDialog.open,
      /**推荐商品购买弹窗 */
      buyGood: async ({ type: configId, ...rest }) => {
        // 获取轻舟的此类弹窗配置
        const config = await getQingzhouConfig({ position: "buy_good_url" });

        const materials = config?.pits?.[0]?.materials;

        if (materials?.length > 0) {
          // 指定的配置信息
          const conf = configId
            ? materials.find((item) => item.id === +configId)
            : materials[0];

          if (conf) {
            const { PayRetentionModal } = await import("../Vip/payRetention");

            PayRetentionModal.open({
              config: conf.material,
              ...rest,
            });
          } else {
            const msg = "指定商品配置不存在";
            console.warn(msg);
            HBMessage.error(msg);
          }
        } else {
          const msg = "配置不存在";
          console.warn(msg);
          HBMessage.error(msg);
        }
      },

      companyConsult: async ({ type }) => {
        const { default: FromDialog } = await import(
          "@/pageComponents/Consult/FormModal"
        );

        const config = await getGrocery("company-consult-v2");

        FromDialog.open({
          config,
          leads_entrance: type || "other",
        });
      },
    };
    const hash = (window.location.hash || "").split("#")[1];

    if (!hash) {
      return;
    }

    const {
      action,
      type,
      selectedPkg,
      selectedPlusSku,
      versionTab,
      selectSkuId,
      buyType,
      source,
    } = parseVipUrlHash(hash);

    if (actionsMap[action]) {
      if (!user?.user_id && action !== "companyConsult") {
        const res = await login({
          source_location: "购买弹窗",
        });
        if (!res) {
          window.location.hash = "";
          return;
        }
      }

      // 获取查询字符串部分
      const params = new URLSearchParams(window.location.search);
      const source_location = params.get("source_location");

      // 关闭其他弹窗，避免相互影响
      destroyAll();
      actionsMap[action]({
        type: type,
        selectedPkg: (selectedPkg || "").split(";"),
        afterClose() {
          window.location.hash = "";
        },
        /**企业会员选中Version（基础授权、高级授权）使用 */
        versionTab: versionTab,
        defaultSelectPlusSku: selectedPlusSku,
        /**稿定支付弹窗使用 */
        selectSkuId,
        buyType,
        source: source || params.get("source"),
        source_location,
      });
    }
  });

  useEffect(() => {
    if (userReady) {
      handleBuyVipHash();
    }
  }, [handleBuyVipHash, userReady]);

  useEffect(() => {
    window.addEventListener("hashchange", handleBuyVipHash);

    router.events.on("hashChangeComplete", handleBuyVipHash);
    router.events.on("routeChangeComplete", handleBuyVipHash);

    return () => {
      window.removeEventListener("hashchange", handleBuyVipHash);
      router.events.off("hashChangeComplete", handleBuyVipHash);
      router.events.off("routeChangeComplete", handleBuyVipHash);
    };
  }, [handleBuyVipHash, router.events]);

  // 轻舟用户商业化场景触达，触发轻舟配置的条件
  useEffect(() => {
    if (!user) return;

    // 会员过期前N天触发
    if (vips.length) {
      stewardInstance.trigerAwaitInit(
        PRESETS_CONDITION.HUABAN_VIP_EXPIRE_BEFORE_DAYS,
        {
          condition: dayjs(user.extra?.material_vip_expire_at).diff(
            dayjs(),
            "day",
            true
          ),
        }
      );
    }

    // 会员过期后N天触发
    if (user.extra?.material_vip_expire_at && !vips.length) {
      stewardInstance.trigerAwaitInit(
        PRESETS_CONDITION.HUABAN_VIP_EXPIRE_AFTER_DAYS,
        {
          condition: dayjs().diff(
            dayjs(user.extra?.material_vip_expire_at),
            "day",
            true
          ),
        }
      );
    }
  }, [user, userReady, vips]);

  useEffect(() => {
    // 未登录不弹
    if (!user) return;

    if (
      [
        "/oauth/authorize",
        "/login",
        "/signup",
        "/bind/tel",
        "/oauth/callback",
        "/share",
        "/gdmerge",
      ].includes(router.pathname)
    ) {
      // 授权页面不弹
      return;
    }

    // 关注页有内容就不弹 已经有职业或者领域的、 或者有关注内容的（画板，用户，专题、来源）用户不再弹出
    if (
      user?.extra?.job_id ||
      user?.extra?.job_ids?.length ||
      user?.following_count ||
      user?.board_following_count ||
      user?.domain_count ||
      user?.explore_following_count ||
      user?.topic_following_count ||
      // 没有组织id不弹, 需要等用户绑定组织id后才弹，避免触发两个弹窗（新手引导弹窗与冷启动弹窗）
      !user?.extra?.org_id
    ) {
      // 全局弹窗
      stewardInstance.trigerAwaitInit(
        PRESETS_CONDITION.CUR_PROJECT_ENTER_TIMES,
        {
          needRemoveTrigger: true,
        }
      );
    }

    // 采用的方案：新手引导页面，是一个强全局弹窗，不可跳过
    // isRegisterOver24H() &&
    //   globalDialogControl.push({
    //     key: "guideDialog",
    //     props: {
    //       closable: false,
    //     },
    //     dialog: GuideDialog,
    //     priority: 0,
    //   });
  }, [router.pathname, user]);

  useCheckVipUpgrade();

  // 轻舟运营条件触发：下载次数
  useEffect(() => {
    if (isNumber(downloadLeft)) {
      stewardInstance.trigerAwaitInit(
        PRESETS_CONDITION.HUABAN_CUR_DOWNLOAD_BALANCE,
        {
          condition: downloadLeft,
        }
      );
    }
  }, [downloadLeft]);

  useGlobalMerge();
};

export default useFirstModal;
